import { Link } from "react-router-dom";
import Layout from "../components/Layout";

// Plans & Membership — authenticated-only surface (2026-08).
//
// Every dollar figure and every human contact channel here is
// authoritative for founder-approved copy. Do NOT change prices,
// clinician limits, consultation caps, or the mailto address without
// a written revision.
//
// Contact channel is the ONLY user-facing action. No Stripe, no
// checkout, no card entry, no telephone. Founder-approved: use the
// existing SUPPORT_EMAIL address that the backend already ships with
// (backend/emails.py). A dedicated business phone line is NOT yet
// published, so no tel: link is rendered anywhere on this page.
const SUPPORT_EMAIL = "support@prosthoai.com";

function mailto(subject) {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

// Monthly memberships — three tiers. See the founder-approved plan
// (Feb 2026). "Practice" tier is metered at 150 consultations / month
// across the practice; "DSO & Enterprise" is quote-only (no $).
const MEMBERSHIPS = [
  {
    slug: "starter",
    name: "Starter",
    price_display: "$99",
    price_suffix: "/ month",
    who: "One clinician getting started with ProsthoAI.",
    bullets: [
      "One clinician seat",
      "All 11 clinical modules",
      "Up to 50 consultations / month",
      "Case history & PDF export",
      "Email support",
      "AI-assisted prosthodontic decision support",
    ],
    cta_label: "Contact support",
    cta_subject: "ProsthoAI Starter plan",
  },
  {
    slug: "practice",
    name: "Practice",
    price_display: "$249",
    price_suffix: "/ month",
    highlight: true,
    who: "Small practices with up to three clinicians sharing ProsthoAI.",
    bullets: [
      "Up to three clinician seats",
      "Everything in Starter",
      "Up to 150 consultations / month across the practice",
      "Treatment-plan templates",
      "Email support",
    ],
    cta_label: "Contact support",
    cta_subject: "ProsthoAI Practice plan",
  },
  {
    slug: "dso-enterprise",
    name: "DSO & Enterprise",
    price_display: "Custom pricing",
    price_suffix: null,
    who:
      "For DSOs, corporations, and multi-location dental organizations. " +
      "User access, locations, expected consultation volume, data " +
      "requirements, and pricing are established through a written " +
      "organizational quote.",
    bullets: [
      "Four or more clinicians, corporate groups, or any multi-location organization",
      "Custom seat, volume, and data terms",
      "Written organizational quote",
    ],
    cta_label: "Request a custom quote",
    cta_subject: "DSO & Enterprise quote request",
  },
];

// Specialist review add-ons — separate from monthly membership.
const REVIEW_TIERS = [
  {
    slug: "quick_assessment",
    tier: 1,
    price_display: "$75",
    name: "Quick Assessment Review",
    detail:
      "Your Quick Photo Assessment personally reviewed by Dr. Ronik " +
      "Seecharan. Fastest specialist checkpoint in the workflow.",
    cta_subject: "Tier 1 Quick Assessment Review",
  },
  {
    slug: "module",
    tier: 2,
    price_display: "$150",
    highlight: true,
    name: "Single Module Review",
    detail:
      "One clinical module consultation personally reviewed by Dr. " +
      "Ronik Seecharan with clinical notes and treatment guidance.",
    cta_subject: "Tier 2 Single Module Review",
  },
  {
    slug: "complete_case",
    tier: 3,
    price_display: "$250",
    name: "Complete Case Review",
    detail:
      "Full-mouth Complete Case Analysis personally reviewed by Dr. " +
      "Ronik Seecharan. Facial analysis, occlusion, full-mouth " +
      "findings, and pathway selection evaluated.",
    cta_subject: "Tier 3 Complete Case Review",
  },
  {
    slug: "interdisciplinary",
    tier: 4,
    price_display: "$395",
    name: "Comprehensive Interdisciplinary Review",
    detail:
      "Multi-specialist case with full synthesis personally reviewed " +
      "by Dr. Ronik Seecharan.",
    cta_subject: "Tier 4 Comprehensive Interdisciplinary Review",
  },
];

export default function PlansMembership() {
  return (
    <Layout>
      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        data-testid="plans-membership-page"
      >
        <div className="mb-8">
          <Link
            to="/dashboard"
            data-testid="plans-back-to-dashboard"
            className="text-sm text-[var(--pa-text-muted,#aaa)] hover:text-[var(--pa-gold,#d4af37)] transition"
          >
            ← Back to Dashboard
          </Link>
        </div>

        <header className="mb-10">
          <h1
            className="font-serif text-3xl sm:text-4xl text-[var(--pa-text,#fff)]"
            data-testid="plans-heading"
          >
            Plans &amp; Membership
          </h1>
          <p className="mt-3 text-sm text-[var(--pa-text-muted,#aaa)] max-w-2xl">
            Choose the option that fits how you use ProsthoAI. All fees below
            are current as of publication. Contact support to change or
            confirm an option — no automatic billing or online payment is
            processed here.
          </p>
        </header>

        {/* Monthly memberships ------------------------------------- */}
        <section
          aria-labelledby="memberships-heading"
          data-testid="memberships-section"
        >
          <h2
            id="memberships-heading"
            data-testid="memberships-heading"
            className="font-serif text-xl sm:text-2xl text-[var(--pa-gold,#d4af37)]"
          >
            Monthly ProsthoAI Membership
          </h2>
          <div
            className="mt-6 grid gap-5 md:grid-cols-3"
            data-testid="memberships-grid"
          >
            {MEMBERSHIPS.map((m) => (
              <article
                key={m.slug}
                data-testid={`plan-${m.slug}`}
                className={
                  "flex flex-col rounded-lg p-6 border " +
                  (m.highlight
                    ? "border-[var(--pa-gold,#d4af37)] bg-[var(--pa-surface,#111)] shadow-[0_0_40px_-12px_rgba(212,175,55,0.35)]"
                    : "border-[var(--pa-border,#333)] bg-[var(--pa-surface,#111)]")
                }
              >
                {m.highlight && (
                  <div className="kicker mb-3 text-[var(--pa-gold,#d4af37)]">
                    Most popular
                  </div>
                )}
                <h3 className="font-serif text-xl text-[var(--pa-text,#fff)]">
                  {m.name}
                </h3>
                <div
                  className="mt-4 flex items-baseline gap-1"
                  data-testid={`plan-${m.slug}-price`}
                >
                  <span className="font-serif text-3xl text-[var(--pa-text,#fff)]">
                    {m.price_display}
                  </span>
                  {m.price_suffix && (
                    <span className="text-sm text-[var(--pa-text-muted,#aaa)]">
                      {m.price_suffix}
                    </span>
                  )}
                </div>
                <p className="mt-4 text-sm text-[var(--pa-text-muted,#aaa)] leading-relaxed">
                  {m.who}
                </p>
                <ul
                  className="mt-4 space-y-2 flex-1"
                  data-testid={`plan-${m.slug}-bullets`}
                >
                  {m.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-sm text-[var(--pa-text,#eee)] leading-snug"
                    >
                      • {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={mailto(m.cta_subject)}
                  data-testid={`plan-${m.slug}-cta`}
                  className={
                    "mt-6 block text-center font-semibold px-4 py-2.5 rounded-md transition " +
                    (m.highlight
                      ? "bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] hover:opacity-90"
                      : "border border-[var(--pa-gold,#d4af37)] text-[var(--pa-gold,#d4af37)] hover:bg-[var(--pa-gold,#d4af37)]/10")
                  }
                >
                  {m.cta_label} →
                </a>
              </article>
            ))}
          </div>
        </section>

        {/* Specialist review add-ons ------------------------------- */}
        <section
          aria-labelledby="reviews-heading"
          data-testid="reviews-section"
          className="mt-16"
        >
          <h2
            id="reviews-heading"
            data-testid="reviews-heading"
            className="font-serif text-xl sm:text-2xl text-[var(--pa-gold,#d4af37)]"
          >
            Optional Specialist Review Add-ons
          </h2>
          <p className="mt-2 text-sm text-[var(--pa-text-muted,#aaa)] max-w-2xl">
            One-time reviews personally reviewed by Dr. Ronik Seecharan.
            Add-ons are separate from your monthly membership.
          </p>
          <div
            className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-4"
            data-testid="reviews-grid"
          >
            {REVIEW_TIERS.map((t) => (
              <article
                key={t.slug}
                data-testid={`review-tier-${t.slug}`}
                className={
                  "flex flex-col rounded-lg p-6 border " +
                  (t.highlight
                    ? "border-[var(--pa-gold,#d4af37)] bg-[var(--pa-surface,#111)] shadow-[0_0_40px_-12px_rgba(212,175,55,0.3)]"
                    : "border-[var(--pa-border,#333)] bg-[var(--pa-surface,#111)]")
                }
              >
                <div className="kicker text-[var(--pa-gold,#d4af37)]">
                  Tier {t.tier}
                </div>
                <h3 className="font-serif text-lg text-[var(--pa-text,#fff)] mt-2 leading-tight">
                  {t.name}
                </h3>
                <div
                  className="mt-3 flex items-baseline gap-1"
                  data-testid={`review-tier-${t.slug}-price`}
                >
                  <span className="font-serif text-3xl text-[var(--pa-text,#fff)]">
                    {t.price_display}
                  </span>
                  <span className="text-xs text-[var(--pa-text-muted,#aaa)]">
                    / review
                  </span>
                </div>
                <p className="mt-3 text-xs text-[var(--pa-text-muted,#aaa)] leading-relaxed flex-1">
                  {t.detail}
                </p>
                <a
                  href={mailto(t.cta_subject)}
                  data-testid={`review-tier-${t.slug}-cta`}
                  className={
                    "mt-5 block text-center text-sm font-semibold px-4 py-2 rounded-md transition " +
                    (t.highlight
                      ? "bg-[var(--pa-gold,#d4af37)] text-[var(--pa-bg,#0a0a0a)] hover:opacity-90"
                      : "border border-[var(--pa-gold,#d4af37)] text-[var(--pa-gold,#d4af37)] hover:bg-[var(--pa-gold,#d4af37)]/10")
                  }
                >
                  Contact support →
                </a>
              </article>
            ))}
          </div>
        </section>

        <p
          className="mt-12 text-xs text-[var(--pa-text-dim,#666)] text-center max-w-3xl mx-auto"
          data-testid="plans-footer-note"
        >
          To choose or change any option, contact{" "}
          <a
            href={mailto("Plans & Membership question")}
            className="text-[var(--pa-gold,#d4af37)] hover:underline"
            data-testid="plans-footer-support-email"
          >
            {SUPPORT_EMAIL}
          </a>
          . No automatic billing or online payment is processed here.
        </p>
      </div>
    </Layout>
  );
}
